import { all, call, put, takeEvery } from 'redux-saga/effects';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import notifications from 'src/redux/notifications/actions';
import { t } from 'src/i18n';
import { isSuccess } from 'src/containers/admin/pricing/rateEngine/rateEngineShared/apiStatus';
import { GATE_FAILED_CODE } from '../constants';
import { requestForAction } from '../shared/actionHelpers';
import actions from './actions';
import actionTypes from './actionTypes';
import type { IGateCondition } from '../sharedTypes';
import type { IInviteRequestPayload, IMutationRequestPayload } from './types';

const HTTP_UNPROCESSABLE = 422;

// The server's own sentence when it sent one. A refusal here is a state change the operator did not
// see — already invited, already removed — and the generic wording explains none of it.
const refusalMessage = (result: { validationMessages?: string[]; messages?: string[] } | undefined): string =>
  result?.validationMessages?.[0] ?? result?.messages?.[0] ?? t('dash.common.notification.something_went_wrong');

export function* invite({ payload }: { type: string; payload: IInviteRequestPayload }): Generator<any, void, any> {
  try {
    const request = requestForAction(payload.endpoint);

    if (!request) {
      yield put(actions.inviteError(t('dash.agencies.contractsV2.errors.action_unavailable')));

      return;
    }

    const result = yield call(sendRequest, request, { data: {} }, undefined);

    if (isSuccess(result)) {
      yield put(actions.inviteSuccess());
      yield put(notifications.success(t('dash.agencies.contractsV2.notifications.invited')));

      return;
    }

    // 422 is the gate: nothing was written, the contract is still a draft, and every condition names
    // something the operator can go and fix. 403 is the other case — the contract already moved on.
    if (result?.status === HTTP_UNPROCESSABLE && result.data?.code === GATE_FAILED_CODE) {
      const conditions: IGateCondition[] = Array.isArray(result.data.unmet_conditions)
        ? result.data.unmet_conditions
        : [];

      // A gate refusal with no conditions is a refusal with nothing to show; the reducer would read it as success.
      if (conditions.length) {
        yield put(actions.inviteGateFailed(conditions));

        return;
      }
    }

    yield put(actions.inviteError(refusalMessage(result)));
  } catch (error) {
    logger.error(error);

    yield put(actions.inviteError(t('dash.common.notification.something_went_wrong')));
  }
}

export function* remove({ payload }: { type: string; payload: IMutationRequestPayload }): Generator<any, void, any> {
  try {
    const request = requestForAction(payload.endpoint);

    if (!request) {
      yield put(actions.removeError(t('dash.agencies.contractsV2.errors.action_unavailable')));

      return;
    }

    const result = yield call(sendRequest, request, {}, undefined);

    if (isSuccess(result)) {
      yield put(actions.removeSuccess());
      yield put(notifications.success(t('dash.agencies.contractsV2.notifications.removed')));

      return;
    }

    // No toast on a refusal: the dialog stays open and carries the reason, and a toast beside it
    // would report one failure twice.
    yield put(actions.removeError(refusalMessage(result)));
  } catch (error) {
    logger.error(error);

    yield put(actions.removeError(t('dash.common.notification.something_went_wrong')));
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actionTypes.INVITE_REQUEST, invite), takeEvery(actionTypes.REMOVE_REQUEST, remove)]);
}
